import React, {useEffect, useState} from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import citiesData from "../../data/cities.json";
import LoadingButton from '../../components/LoadingButton';
import { logEvent } from "../../utils/analytics";
import * as fbq from '../../utils/FacebookPixel';
import { EDUCATION_API_URL } from "../../constants";
import {registrationRequest, trackRegistration} from './registrationRequest';

interface Step1Props {
    nextStep: () => void;
    handleSchoolData: (data: any) => void;
}

const Step1: React.FC<Step1Props> = ({ nextStep, handleSchoolData }) => {
    const [districts, setDistricts] = useState<string[]>([]);

    const [requestError, setRequestError] = useState('');

    const validationSchema = Yup.object({
        name: Yup.string().trim().required("Okul adı zorunludur."),
        city: Yup.string().required("Şehir seçimi zorunludur."),
        district: Yup.string().required("İlçe seçimi zorunludur."),
    });

    const formik = useFormik({
        initialValues: {
            name: "",
            city: "",
            district: "",
            country: "Türkiye",
        },
        validationSchema,
        onSubmit: async (values) => {
            setRequestError('');
            const mergedAddress = `${values.district}, ${values.city}`;
            try {
                const payload = {
                    active: true,
                    name: values.name.trim(),
                    cityCode: values.city,
                    zipCode: "34500",
                    address: mergedAddress,
                    organizationId: 1,
                    recommendedSchoolId: 3,
                    createdUserId: 1
                };

                const data = await registrationRequest(EDUCATION_API_URL + 'school', payload,
                    'Okul kaydedilemedi. Bilgilerinizi kontrol edip tekrar deneyin.',
                    'Bu okul adı zaten kullanılıyor. Okul adınızı kontrol edin.');
                handleSchoolData(data);
                trackRegistration(() => {
                    logEvent("Button", "Clicked", "Create School");
                    fbq.event('CreateSchool', {
                        content_name: 'School Creation',
                        content_category: 'Onboarding',
                        school_name: values.name,
                        city: values.city
                    });

                    window.lintrk?.('track', { conversion_id: 21977081 });
                });
                nextStep();
            } catch (error) {
                setRequestError((error as Error).message);
            }
        },
    });

    useEffect(() => {
        const selectedCity = citiesData.cities.find(city => city.name === formik.values.city);
        setDistricts(selectedCity?.districts || []);
        formik.setFieldValue('district', '');
        // The district must reset only when the selected city changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.city]);

    return (
        <div className="form-container">
            <h2>Kidly'ye Hoş Geldin!</h2>
            <p>Kidly Kütüphanesi'ne erişmek için önce bir hesap oluşturmanız gerekiyor. <br/> Lütfen aşağıdaki bilgileri
                doldurarak başlayın.</p>

            <p>
                Kidly Okul'a kayıt olmak çok kolay. Dilerseniz bu rehberden destek alabilirsiniz: <br/>
                <a href="https://youtu.be/s0pGZee8ygk?si=qj2GduxRn5ubUD71&t=64">
                    https://youtu.be/s0pGZee8ygk?si=qj2GduxRn5ubUD71&t=64
                </a> <br/><br/>
            </p>
            <div>
                <label htmlFor="name">Okul Adı
                    {formik.touched.name && formik.errors.name && (
                        <div className="error">{formik.errors.name}</div>
                    )}
                </label>
                <input
                    id="name"
                    name="name"
                    type="text"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.name}
                />

                <div className="location-selects">
                    <div className="select-group">
                        <label htmlFor="city">
                            Şehir
                            {formik.touched.city && formik.errors.city && (
                                <div className="error">{formik.errors.city}</div>
                            )}
                        </label>
                        <select
                            id="city"
                            name="city"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.city}
                        >
                            <option value="">Şehir Seçiniz</option>
                            {citiesData.cities.map((city) => (
                                <option key={city.id} value={city.name}>
                                    {city.name}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="select-group">
                        <label htmlFor="district">
                            İlçe
                            {formik.touched.district && formik.errors.district && (
                                <div className="error">{formik.errors.district}</div>
                            )}
                        </label>
                        <select
                            id="district"
                            name="district"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.district}
                            disabled={!formik.values.city}
                        >
                            <option value="">İlçe Seçiniz</option>
                            {districts.map((district) => (
                                <option key={district} value={district}>
                                    {district}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                {requestError && <div className="alert alert-danger" role="alert">{requestError}</div>}
                <LoadingButton onClick={async () => await formik.submitForm()}>
                    <span>Sonraki adım</span>
                </LoadingButton>
            </div>
        </div>
    );
};

export default Step1;
